import { LitElement, TemplateResult, html } from 'lit';
import {
  customElement,
  property,
  queryAssignedElements,
  state,
} from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import encapsulatedStyles from './styles/outline-feature.encapsulated.css?inline';
import globalStyles from './styles/outline-feature.global.css?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

export type mediaPosition = 'left' | 'right';

/**
 * The OutlineFeature component
 * @element outline-feature
 *
 * @extends LitElement
 *
 * @slot - The feature's main content.
 * @slot media - An optional media to render at the left or right side of the feature.
 * @slot header - An optional header for the feature.
 * @slot footer - An optional footer for the feature.
 *
 * @property media-position - It determines the position of the media element (such as an image) within the card.
 *
 * @cssproperty --feature--content-padding - The padding to use for the feature's content.
 * @cssproperty --feature--content-gap - The gap to use for the feature's content elements.
 * @cssproperty --feature--media-width - The width of the feature's media element.
 * @cssproperty --feature--header-gap - The gap to use for the feature's header.
 * @cssproperty --feature--footer-gap - The gap to use for the feature's footer.
 */
@customElement('outline-feature')
export class OutlineFeature extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  @queryAssignedElements({ slot: 'media' })
  _mediaElements!: Array<HTMLElement>;

  @property({ attribute: 'media-position' })
  mediaPosition: mediaPosition = 'left';

  @state()
  hasMediaElement = false;

  protected firstUpdated(): void {
    this.hasMediaElement = !!this._mediaElements.length;
  }

  render(): TemplateResult {
    const classes = {
      'feature': true,
      [`feature--media-${this.mediaPosition}`]: true,
      'feature--has-media': this.hasMediaElement,
    };

    return html`
      <div class="${classMap(classes)}">
        <div class="feature__media">
          <slot name="media"></slot>
        </div>
        <div class="feature__content">
          <slot name="header"></slot>
          <slot></slot>
          <slot name="footer"></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-feature': OutlineFeature;
  }
}
